export interface RowData {
  sku?: string;
  sku_detail?: string;
  nest_no?: number;
  brand?: string;
  beer_type?: string;
  ptc_segment?: string;
  price_per_ltr?: number;
  pack_size?: number;
  pack_type?: string;
  pack_quantity?: number;
  volume?: string;
  revenue?: string;
  cluster_name?: string;
  cluster_level?: string;
}

export interface NestGroups {
  label: string;
  value: string;
  isSelected: boolean;
}

export interface CardConfig {
  header: {
    nestNumber: number;
    pplRange: string;
    beerType: number | string;
  };
  stats: {
    label: string;
    value: number | string;
  }[];
  columns: string[];
  rows: RowData[];
}

export interface NestGroupConfig {
  nestGroupValue: string;
  cardConfigs: CardConfig[];
}

export interface NestData {
  count_of_Skus: string;
  count_of_nests: number;
  "%Count"?: number;
}

function getNestingGroupData(nestingData: any[]): CardConfig[] {
  const groupedByNest = nestingData.reduce(
    (acc, item) => {
      const nestNo = item.nest_no;
      if (!acc[nestNo]) acc[nestNo] = [];
      acc[nestNo].push(item);
      return acc;
    },
    {} as Record<number, any[]>,
  );

  return Object.entries(groupedByNest).map(([nestNo, items]) => {
    const nestItems = items as any[];
    const metrics = collectNestMetrics(nestItems);
    const pplRange = computePplRange(metrics.ppls);

    return {
      header: {
        nestNumber: Number(nestNo),
        pplRange,
        beerType: metrics.beerTypes.size,
      },
      stats: [
        { label: "# Brand", value: metrics.brands.size },
        { label: "# Segment", value: metrics.segments.size },
        { label: "# Pack Type", value: metrics.packTypes.size },
        { label: "# Pack Size", value: metrics.packSizes.size },
      ],
      columns: ["SKU", "PPL", "Segment", "Brand"],
      rows: metrics.rows,
    };
  });
}

function collectNestMetrics(nestItems: any[]) {
  const beerTypes = new Set<string>(
    nestItems
      .map((i) => i.beer_type)
      .filter((v) => v !== undefined && v !== null && v !== "") as string[],
  );
  const segments = new Set<string>(
    nestItems
      .map((i) => i.ptc_segment)
      .filter((v) => v !== undefined && v !== null && v !== "") as string[],
  );
  const brands = new Set<string>(
    nestItems
      .map((i) => i.brand)
      .filter((v) => v !== undefined && v !== null && v !== "") as string[],
  );
  const packTypes = new Set<string>(
    nestItems
      .map((i) => i.pack_type)
      .filter((v) => v !== undefined && v !== null && v !== "") as string[],
  );
  const packSizes = new Set<number>(
    nestItems
      .map((i) => i.pack_size)
      .filter((v) => v !== undefined && v !== null) as number[],
  );

  const ppls: number[] = nestItems.reduce((acc: number[], item) => {
    if (item.price_per_ltr == null) return acc;
    const ppl =
      typeof item.price_per_ltr === "string"
        ? parseFloat(String(item.price_per_ltr).replace(/,/g, ""))
        : item.price_per_ltr;
    if (!isNaN(ppl)) acc.push(ppl);
    return acc;
  }, [] as number[]);

  const rows: RowData[] = nestItems
    .filter(
      (item) =>
        item.price_per_ltr != null ||
        item.revenue != null ||
        item.volume != null,
    )
    .map((item) => ({
      sku: item.sku,
      sku_detail: item.sku_detail,
      nest_no: item.nest_no,
      brand: item.brand,
      beer_type: item.beer_type,
      ptc_segment: item.ptc_segment,
      price_per_ltr: item.price_per_ltr,
      pack_size: item.pack_size,
      pack_type: item.pack_type,
      pack_quantity: item.pack_quantity,
      volume: item.volume,
      revenue: item.revenue,
      cluster_name: item.cluster_name,
      cluster_level: item.cluster_level,
    }));

  return { beerTypes, segments, brands, packTypes, ppls, packSizes, rows };
}

function computePplRange(ppls: number[]) {
  if (!ppls || ppls.length === 0) return "-";
  const minPpl = Math.min(...ppls);
  const maxPpl = Math.max(...ppls);
  if (isNaN(minPpl) || isNaN(maxPpl)) return "-";
  return minPpl === maxPpl ? `${minPpl}` : `${minPpl} - ${maxPpl}`;
}

export function getNestGroupConfigs(nestingData: any[]): NestGroupConfig[] {
  const zeroToFive: any[] = [];
  const sixToTen: any[] = [];
  const moreThanTen: any[] = [];

  for (const item of nestingData) {
    const nestNo = item.nest_no;
    if (nestNo >= 0 && nestNo <= 5) {
      zeroToFive.push(item);
    } else if (nestNo >= 6 && nestNo <= 10) {
      sixToTen.push(item);
    } else if (nestNo >= 11) {
      moreThanTen.push(item);
    }
  }

  return [
    {
      nestGroupValue: "zeroToFive",
      cardConfigs: getNestingGroupData(zeroToFive),
    },
    {
      nestGroupValue: "sixToTen",
      cardConfigs: getNestingGroupData(sixToTen),
    },
    {
      nestGroupValue: "moreThanTen",
      cardConfigs: getNestingGroupData(moreThanTen),
    },
  ];
}

export function getTotalSkuNests(nestingData: any[]): number {
  const uniqueNests = new Set<number>();
  for (const item of nestingData) {
    uniqueNests.add(item.nest_no);
  }
  return uniqueNests.size;
}

export function getAllCardConfigs(
  nestGroupConfigs: NestGroupConfig[],
): CardConfig[] {
  const allCards: CardConfig[] = [];
  for (const group of nestGroupConfigs) {
    allCards.push(...group.cardConfigs);
  }
  return allCards;
}
